import React from 'react';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheck, Bell, LogOut } from 'lucide-react';
import { useLoneStarAuth } from '@/context/LoneStarAuthContext';
import NotificationBell from '@/components/NotificationBell';
import LoneStarOverview from './LoneStarOverview';

/**
 * Minimal Lone Star cross-brand bridge dashboard.
 * NOTE: this is intentionally scoped to the cross-brand notification
 * bridge only (not a full clone of the Lone Star operations dashboard —
 * that already exists in its own reference codebase). It exists so an
 * authorized manager who switches over from Amigos has a real, separate,
 * branded landing surface with its own session, per the cross-brand
 * alerts + SSO switching requirement.
 */
const LoneStarApp = () => {
  const { user, logout, loading, token } = useLoneStarAuth();
  const navigate = useNavigate();

  if (loading) return <div className="dash-body" style={{ minHeight: '100vh' }} />;
  if (!user) return <Navigate to="/lonestar/login" replace />;

  const doLogout = () => { logout(); navigate('/lonestar/login'); };

  return (
    <div className="amigos-app dash-body">
      <div className="dash-shell">
        <aside className="dash-sidebar open" style={{ position: 'fixed' }} data-testid="lonestar-sidebar">
          <div className="dash-sidebar__brand" style={{ gap: '0.5rem' }}>
            <ShieldCheck size={26} color="var(--gold)" />
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', color: 'var(--text-primary)', lineHeight: 1.1 }}>LONE STAR</div>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cross-Brand Bridge</div>
            </div>
          </div>
          <nav className="dash-sidebar__nav">
            <NavLink to="/lonestar/overview" className={({ isActive }) => `dash-nav-link ${isActive ? 'active' : ''}`} data-testid="lonestar-nav-overview">
              <Bell size={17} /> Amigos Alerts
            </NavLink>
          </nav>
          <div className="dash-sidebar__footer">
            <div className="dash-user-chip">
              <div className="dash-user-avatar">{user.name?.[0]?.toUpperCase() || 'U'}</div>
              <div>
                <div className="dash-user-name" data-testid="lonestar-user-name">{user.name}</div>
                <div className="dash-user-role">{user.role?.replace('_', ' ')}</div>
              </div>
            </div>
            <button className="dash-btn" style={{ width: '100%' }} onClick={doLogout} data-testid="lonestar-logout-btn"><LogOut size={15} /> Sign Out</button>
          </div>
        </aside>

        <div className="dash-main">
          <div className="dash-topbar">
            <h1>Cross-Brand Bridge</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
              <NotificationBell token={token} />
              <span className="role-badge role-manager">{user.role?.replace('_', ' ')}</span>
            </div>
          </div>
          <div className="dash-content">
            <Routes>
              <Route path="overview" element={<LoneStarOverview />} />
              <Route path="*" element={<Navigate to="overview" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoneStarApp;
